/**
 * 「開発pt (MTD)」カードの表示ビュー（全部 / 要件定義抜き / 内訳）の定義と、
 * 要件定義系ビューを出してよい team スコープの判定。
 *
 * 2026-10 の開発フロー変更で要件定義は開発ptとは**別管理**になった（fibo予算 `_PDG_v2` が
 * 「要件定義」と「開発pt（要件定義を除く）」を別行で持つ）。要件定義工程は PDG 側にしか
 * 無く、ACE の予算シートには対応する行が存在しないため、ビュー切替も PDG スコープに限る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 開発pt (MTD) カードのビュー識別子・要件定義ビューの対象 team 判定・要件定義/開発ptの配色を定義
 * @graph-connects none
 */

import type { KpiTeam } from '../../../types/api.js';

/**
 * 「開発pt (MTD)」カードの表示ビュー。
 *  - `all`: 従来どおり 開発pt 全体（目標(予算) / 目標(キャパ) / 実績）
 *  - `excludeRequirement`: 目標(予算) を `_PDG_v2` の「開発pt（要件定義を除く）」に差し替え、
 *    実績からも要件定義セット子票の pt を除いた線にする。目標(キャパ) 線は出さない
 *    （キャパは要件定義を含む総量で、控除後の予算 / 実績と同じ軸に並べると過達に見えるため）
 *  - `breakdown`: 要件定義 と それ以外の開発pt の比率（円）と、現時点進捗目標の予算 vs 実績（棒）を出す
 * @graph-connects none
 */
export type DevelopPointView = 'all' | 'excludeRequirement' | 'breakdown';

/**
 * 開発pt のビュー既定値。KPI の口径を「開発pt（要件定義を除く）」に固定し、役員会の
 * KPI 管理SS（週次目標 = 板SS『目標(FY27)』修正目標の按分）と同じ口径で見せる。
 * 要件定義ビュー非対応スコープ（ACE 系）では各カードが「全部」に読み替える。
 * @graph-connects none
 */
export const DEFAULT_DEVELOP_POINT_VIEW: DevelopPointView = 'excludeRequirement';

/**
 * 要件定義系ビューを表示する team スコープ。
 *
 * 全体（`all`）と PDG グループ（PDG / TE / TE サブチーム / SRE）。要件定義工程は PDG 側に
 * しか無く ACE の予算シートには対応する行が無いため、**ACE 単独・ACE サブチームでは出さない**。
 *
 * 全体ビューは「要件定義を除く予算 = PDG の控除後 + ACE の調整後 供給pt 満額」という組み方に
 * なる（ACE 側に控除すべき要件定義が無いため）。PDG 分だけ控除された非対称な線に見えるが、
 * `要件定義予算 + 要件定義を除く予算 = 全体の開発pt予算` は保たれる。
 * @graph-connects none
 */
const REQUIREMENT_VIEW_TEAMS: readonly (KpiTeam | 'all')[] = [
  'all',
  'pdg',
  'tet',
  'growth',
  'experience',
  'sre',
];

/**
 * その team スコープで要件定義系ビュー（要件定義抜き / 内訳）を出せるか。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business team スコープが PDG 系（要件定義予算行を持つ）かを判定し、ビュー切替の表示可否を決める
 * @graph-connects none
 */
export function supportsRequirementView(team: KpiTeam | 'all' | undefined): boolean {
  return team !== undefined && REQUIREMENT_VIEW_TEAMS.includes(team);
}

/**
 * 要件定義pt の系統色（浅い水色）。目標(キャパ)線と同じ `#7dd3fc` を使い、
 * 「要件定義 = 水色」をカード内（円・棒）で一貫させる。
 * @graph-connects none
 */
export const REQUIREMENT_COLOR = '#7dd3fc';

/**
 * 要件定義を除く開発pt の系統色（黄）。週次pt集計の TE 内訳と同じ `#EDC818`。
 * @graph-connects none
 */
export const DEVELOP_COLOR = '#EDC818';
